import {
    IEditor,
    PluginEvent,
    PluginEventType,
    ChangeSource,
    ContextMenuProvider,
} from 'roosterjs-editor-types';
import { safeInstanceOf } from 'roosterjs-editor-dom';
import {
    imageSizeBestFit,
    imageSizeSmall,
    imageSizeMedium,
    imageSizeOriginal,
    imageContextMenuItemSize,
    imageContextMenuRemove,
    imageContextAddAltText,
} from './InlineImageContextMenu.locstring.json';
import { lazyShowImageAltTextDialog } from 'owa-editor-ribbon-secondary-ui';
import loc from 'owa-localize';
import { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';

const IMAGE_SIZE_ATTRIBUTE_NAME = 'data-imgsize';
const IMAGE_SIZE_SMALL = 'small';
const IMAGE_SIZE_MEDIUM = 'medium';
const IMAGE_SIZE_BESTFIT = 'bestfit';
const IMAGE_SIZE_ORIGINAL = 'original';

export default class InlineImageResizePlugin implements ContextMenuProvider<IContextualMenuItem> {
    private editor: IEditor;
    private image: HTMLImageElement;

    public getName() {
        return 'InlineImageResize';
    }

    public initialize(editor: IEditor): void {
        this.editor = editor;
    }

    public dispose(): void {
        this.editor = null;
        this.image = null;
    }

    public onPluginEvent(event: PluginEvent) {
        if (event.eventType == PluginEventType.ContentChanged) {
            if (
                event.source == ChangeSource.ImageResize &&
                safeInstanceOf(event.data, 'HTMLImageElement')
            ) {
                event.data.removeAttribute(IMAGE_SIZE_ATTRIBUTE_NAME);
            }
        }
    }

    public getContextMenuItems(image: Node) {
        if (safeInstanceOf(image, 'HTMLImageElement')) {
            this.image = image;
            const currentSizeSetting = image.getAttribute(IMAGE_SIZE_ATTRIBUTE_NAME);
            const menuNameMap = {
                [IMAGE_SIZE_BESTFIT]: loc(imageSizeBestFit),
                [IMAGE_SIZE_SMALL]: loc(imageSizeSmall),
                [IMAGE_SIZE_MEDIUM]: loc(imageSizeMedium),
                [IMAGE_SIZE_ORIGINAL]: loc(imageSizeOriginal),
            };

            return [
                {
                    name: loc(imageContextMenuItemSize),
                    key: 'resize',
                    subMenuProps: {
                        items: Object.keys(menuNameMap).map(name => ({
                            name: menuNameMap[name],
                            key: name,
                            onClick: this.setImageSize,
                            isChecked: currentSizeSetting == name,
                            canCheck: true,
                        })),
                    },
                },
                {
                    name: loc(imageContextMenuRemove),
                    key: 'remove',
                    onClick: this.onRemoveImage,
                },
                {
                    name: loc(imageContextAddAltText),
                    key: 'addAltText',
                    onClick: this.onAltText,
                },
            ];
        } else {
            return null;
        }
    }

    private onRemoveImage = () => {
        if (this.image && this.editor) {
            this.editor.addUndoSnapshot(() => {
                this.editor.deleteNode(this.image);
            });
            this.image = null;
        }
    };

    private setImageSize = (ev: React.MouseEvent<HTMLElement>, item: IContextualMenuItem) => {
        const imageSize = item.key;
        let width: string = '';
        let maxWidth: string = '';

        if (this.image && this.editor) {
            switch (imageSize) {
                case IMAGE_SIZE_SMALL:
                    width = this.image.naturalWidth / 4 + 'px';
                    break;
                case IMAGE_SIZE_MEDIUM:
                    width = this.image.naturalWidth / 2 + 'px';
                    break;
                case IMAGE_SIZE_BESTFIT:
                    maxWidth = '100%';
                    break;
                case IMAGE_SIZE_ORIGINAL:
                    width = this.image.naturalWidth + 'px';
                    break;
            }

            this.editor.addUndoSnapshot(() => {
                /**
                 * Remove explicit width & height attributes on the imageElement. Compensates for images
                 * that pasted from websites that account for image shapes on load
                 *
                 * (e.g. <img width="30" height="30" .../>)
                 */
                this.image.removeAttribute('width');
                this.image.removeAttribute('height');
                this.image.style.width = width;
                this.image.style.maxWidth = maxWidth;
                this.image.style.height = '';
                this.image.setAttribute(IMAGE_SIZE_ATTRIBUTE_NAME, imageSize);
            });
            this.image = null;
        }
    };

    private onAltText = () => {
        lazyShowImageAltTextDialog.import().then(showImageAltTextDialog => {
            // Add null check in the case of slow network.
            // Image is deleted before dialog is imported.
            if (this.image && this.editor) {
                showImageAltTextDialog(this.image, this.editor.getDocument().defaultView, alt => {
                    if (this.image) {
                        this.image.alt = alt;
                        this.image = null;
                    }
                });
            }
        });
    };
}
